import { type ReactNode } from 'react';
import type { UserRole } from '@timesheet/shared';
import { ROLE_LEVEL } from '@timesheet/shared';
import { useAuth } from '@/hooks/useAuth';
import { Alert } from '@/components/ui/Alert';

interface RequireRoleProps {
  /** Either an explicit allow-list, or a minimum role level. Pick one. */
  anyOf?: UserRole[];
  atLeast?: UserRole;
  children: ReactNode;
}

/**
 * Render `children` only if the current user satisfies the role requirement.
 * Otherwise render a 403 alert. Must be inside a RequireAuth boundary.
 */
export function RequireRole({ anyOf, atLeast, children }: RequireRoleProps) {
  const { user } = useAuth();
  if (!user) {
    // Shouldn't happen if used inside RequireAuth, but guard defensively.
    return null;
  }

  let allowed = true;
  if (anyOf && anyOf.length > 0) {
    allowed = anyOf.includes(user.role);
  } else if (atLeast) {
    allowed = ROLE_LEVEL[user.role] >= ROLE_LEVEL[atLeast];
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md p-8">
        <Alert tone="danger" title="Access denied">
          Your role ({user.role}) doesn't have permission to view this page.
        </Alert>
      </div>
    );
  }
  return <>{children}</>;
}
